import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, MessageSquare, CheckCircle2, Calendar, XCircle, Loader2, Power, HelpCircle, RefreshCw } from 'lucide-react';
import OnboardingTour from '@/components/OnboardingTour';
import SupportModal from '@/components/SupportModal';

export default function ProviderDashboard() {
  const { user } = useContext(AuthContext);
  const [reviewStats, setReviewStats] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [availabilityStatus, setAvailabilityStatus] = useState('AVAILABLE');
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoadingData(true);
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      
      const [appointmentsRes, availabilityRes, statsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/provider/appointments?date=${today}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { appointments: [] } })),
        axios.get(`${API}/provider/availability-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { availability_status: 'AVAILABLE' } })),
        axios.get(`${API}/reviews/provider/${user.id}/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/reviews/provider/${user.id}?limit=3`).catch(() => ({ data: { reviews: [] } }))
      ]);
      
      setTodayAppointments(appointmentsRes.data?.appointments || []);
      setAvailabilityStatus(availabilityRes.data?.availability_status || 'AVAILABLE');
      
      if (statsRes.data) setReviewStats(statsRes.data);
      if (reviewsRes.data?.reviews) setRecentReviews(reviewsRes.data.reviews);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const toggleAvailability = async () => {
    setLoadingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const newStatus = availabilityStatus === 'AVAILABLE' ? 'UNAVAILABLE' : 'AVAILABLE';
      
      const res = await axios.post(
        `${API}/provider/availability-status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAvailabilityStatus(res.data.availability_status);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setLoadingStatus(false);
    }
  };

  const isAvailable = availabilityStatus === 'AVAILABLE';

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < Math.round(rating) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <div className="p-4 md:p-6">
      {/* ==================== ONBOARDING TOUR (OVERLAY) ==================== */}
      <OnboardingTour userType="provider" />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Olá, {user?.name}!</h1>
            <p className="text-slate-500">Bem-vindo ao seu painel</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setSupportModalOpen(true)}
              title="Precisa de ajuda?"
              data-testid="support-btn"
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={fetchData}
              disabled={loadingData}
            >
              <RefreshCw className={`w-4 h-4 ${loadingData ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Status Card - Provider Availability System */}
        <Card className={`p-6 mb-6 ${isAvailable ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-300'}`}
              data-testid="availability-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isAvailable ? (
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-gray-500" />
              )}
              <div>
                <h2 className={`text-xl font-semibold ${isAvailable ? 'text-green-800' : 'text-gray-700'}`}>
                  Status de Disponibilidade
                </h2>
                <p className={`${isAvailable ? 'text-green-600' : 'text-gray-500'}`}>
                  {isAvailable ? 'Disponível para novos serviços' : 'Indisponível - Não aparece na busca'}
                </p>
              </div>
            </div>
            <Button 
              variant={isAvailable ? 'outline' : 'default'}
              onClick={toggleAvailability}
              disabled={loadingStatus}
              className={`min-w-[180px] ${!isAvailable ? 'bg-green-600 hover:bg-green-700' : ''}`}
              data-testid="toggle-availability-btn"
            >
              {loadingStatus ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Power className="w-4 h-4 mr-2" />
              )}
              {isAvailable ? 'Ficar Indisponível' : 'Ficar Disponível'}
            </Button>
          </div>
          
          {/* Info adicional quando indisponível */}
          {!isAvailable && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Atenção:</strong> Você não está aparecendo na busca de clientes e não receberá novos agendamentos. 
                Seus serviços já confirmados continuam válidos.
              </p>
            </div>
          )}
        </Card>

        {/* Serviços Hoje */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Serviços Hoje
            </h2>
            <span className="text-2xl font-bold text-primary">{todayAppointments.length}</span>
          </div>
          
          {todayAppointments.length > 0 ? (
            <div className="space-y-2">
              {todayAppointments.slice(0, 3).map((apt) => (
                <div key={apt.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">{apt.client_name || 'Cliente'}</p>
                    <p className="text-sm text-slate-500">{apt.time} - {apt.service_type}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    apt.status === 'CONFIRMED' ? 'bg-green-100 text-green-700' :
                    apt.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {apt.status}
                  </span>
                </div>
              ))}
              {todayAppointments.length > 3 && (
                <p className="text-sm text-slate-500 text-center">
                  +{todayAppointments.length - 3} mais serviços
                </p>
              )}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">Nenhum serviço agendado para hoje</p>
          )}
        </Card>

        {/* Card de Avaliação Resumida */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Avaliação
            </h2>
            <div className="flex items-center gap-3">
              {reviewStats?.total > 0 ? (
                <>
                  <p className="text-3xl font-bold text-primary">
                    {reviewStats.averages?.geral?.toFixed(1) || '0.0'}
                  </p>
                  <div>
                    <div className="flex">
                      {renderStars(reviewStats.averages?.geral || 0)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      ({reviewStats.total} avaliação{reviewStats.total !== 1 ? 'ões' : ''})
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-2xl font-bold text-muted-foreground">Novo</p>
              )}
            </div>
          </div>
        </Card>

        {/* Seção de Avaliações - Histórico Operacional */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-500" />
              Últimas Avaliações
            </h2>
            {reviewStats?.total > 0 && (
              <span className="text-sm text-muted-foreground">
                {reviewStats.total} avaliação{reviewStats.total !== 1 ? 'ões' : ''}
              </span>
            )}
          </div>
          
          {reviewStats?.total > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 pb-4 border-b">
                <div className="text-center">
                  <p className="text-4xl font-bold text-primary">{reviewStats.averages?.geral?.toFixed(1) || '0.0'}</p>
                  <div className="flex justify-center mt-1">
                    {renderStars(reviewStats.averages?.geral || 0)}
                  </div>
                </div>
                <div className="flex-1 grid grid-cols-3 gap-2 text-sm">
                  <div className="text-center p-2 bg-slate-50 rounded">
                    <p className="font-medium">{reviewStats.averages?.lavagem?.toFixed(1) || '-'}</p>
                    <p className="text-xs text-muted-foreground">Lavagem</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 rounded">
                    <p className="font-medium">{reviewStats.averages?.atendimento?.toFixed(1) || '-'}</p>
                    <p className="text-xs text-muted-foreground">Atendimento</p>
                  </div>
                  <div className="text-center p-2 bg-slate-50 rounded">
                    <p className="font-medium">{reviewStats.averages?.local?.toFixed(1) || '-'}</p>
                    <p className="text-xs text-muted-foreground">Local</p>
                  </div>
                </div>
              </div>
              
              {recentReviews.length > 0 && (
                <div className="space-y-3">
                  {recentReviews.map((review) => (
                    <div key={review.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{review.client_name}</span>
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                          <span className="text-sm font-medium">{review.rating_geral || review.rating}</span>
                        </div>
                      </div>
                      {review.comentario && (
                        <p className="text-sm text-muted-foreground">"{review.comentario}"</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>Você ainda não recebeu avaliações.</p>
              <p className="text-sm">Complete serviços para receber feedback dos clientes!</p>
            </div>
          )}
        </Card>
      </div>
      
      {/* Modal de Suporte */}
      <SupportModal 
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        onTicketCreated={() => toast.success('Ticket criado!')}
      />
    </div>
  );
}
